import getConfig from 'next/config';
import { subjects } from 'shared/constants/subjects';
import { grades } from 'shared/constants/grades';
import { AnswerType, QuestionType } from 'views/Questions/Question.types';
import { Question as ListQuestion } from 'types/Questions.types';
import { Api } from 'api/otvety';

const { publicRuntimeConfig } = getConfig();

/**
 * База для построения канонических URL. В проде это https://uchi.ru/otvety,
 * в окружении разработки — соответствующий stage-хост.
 */
const HOST = (publicRuntimeConfig?.HOST_URL || 'https://uchi.ru/otvety').replace(
  /\/$/,
  '',
);

const ORGANIZATION_ID = 'https://uchi.ru/#organization';
const ORGANIZATION_NAME = 'Учи.ру';
const ORGANIZATION_URL = 'https://uchi.ru/';

const stripHtml = (value: string): string =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const getSubjectNameBySlug = (slug: string): string =>
  subjects.find((item) => item.link === slug)?.name || slug;

export const getGradeLabel = (grade: number): string =>
  grades.find((item) => item.value === String(grade))?.label || `${grade} класс`;

export const getQuestionUrl = (slug: string): string => `${HOST}/questions/${slug}`;

type JsonLdOrganization = {
  '@type': 'Organization';
  '@id': string;
  name: string;
  url: string;
};

type JsonLdReference = { '@id': string };

type JsonLdPerson = { '@type': 'Person'; name: string };

type JsonLdOrganizationAuthor = { '@type': 'Organization'; name: string };

type JsonLdAnswer = {
  '@type': 'Answer';
  text: string;
  author: JsonLdPerson | JsonLdOrganizationAuthor;
  dateCreated?: string;
};

type JsonLdQuestion = {
  '@type': 'Question';
  '@id': string;
  name: string;
  text: string;
  author: { '@type': 'Person'; name: string };
  dateCreated: string;
  about: string;
  educationalLevel: string;
  inLanguage: string;
  publisher: JsonLdReference;
  suggestedAnswer: JsonLdAnswer[];
};

type JsonLdWebPage = {
  '@type': 'WebPage';
  '@id': string;
  url: string;
  mainEntity: JsonLdReference;
};

type JsonLdQaPage = {
  '@type': 'QAPage';
  '@id': string;
  mainEntity: JsonLdReference;
};

export type BuildQuestionPageJsonLdResult = {
  '@context': 'https://schema.org';
  '@graph': [JsonLdOrganization, JsonLdWebPage, JsonLdQaPage, JsonLdQuestion];
};

export const buildQuestionPageJsonLd = (
  question: QuestionType,
  answers: AnswerType[],
  marusiaAnswer?: Api.GetMarusiaData200Response.MarusiaAnswer,
): BuildQuestionPageJsonLdResult => {
  const pageUrl = getQuestionUrl(question.slug);
  const questionId = `${pageUrl}#question`;

  const organization: JsonLdOrganization = {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: ORGANIZATION_NAME,
    url: ORGANIZATION_URL,
  };

  const webPage: JsonLdWebPage = {
    '@type': 'WebPage',
    '@id': `${pageUrl}#question-page`,
    url: pageUrl,
    mainEntity: { '@id': questionId },
  };

  const qaPage: JsonLdQaPage = {
    '@type': 'QAPage',
    '@id': `${pageUrl}#qapage`,
    mainEntity: { '@id': questionId },
  };

  const suggestedAnswer: JsonLdAnswer[] = answers.map((answer) => ({
    '@type': 'Answer',
    text: stripHtml(answer.body),
    author: { '@type': 'Person', name: answer.autorName },
    dateCreated: answer.createdAt,
  }));

  if (marusiaAnswer?.text) {
    suggestedAnswer.push({
      '@type': 'Answer',
      text: stripHtml(marusiaAnswer.text),
      author: { '@type': 'Organization', name: 'Маруся' },
    });
  }

  const questionNode: JsonLdQuestion = {
    '@type': 'Question',
    '@id': questionId,
    name: question.body,
    text: question.body,
    author: { '@type': 'Person', name: question.authorName },
    dateCreated: question.createdAt,
    about: getSubjectNameBySlug(question.category.slug),
    educationalLevel: getGradeLabel(question.grade),
    inLanguage: 'ru',
    publisher: { '@id': ORGANIZATION_ID },
    suggestedAnswer,
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [organization, webPage, qaPage, questionNode],
  };
};

type JsonLdListItem = {
  '@type': 'ListItem';
  position: number;
  url: string;
  name: string;
};

export type BuildItemListJsonLdResult = {
  '@context': 'https://schema.org';
  '@type': 'ItemList';
  itemListElement: JsonLdListItem[];
};

export const buildItemListJsonLd = (
  questions: ListQuestion[],
): BuildItemListJsonLdResult => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: questions.map((question, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: getQuestionUrl(question.slug),
    name: question.body,
  })),
});
